import React, { useState, useEffect } from 'react';
import { Task, CreateTaskDto } from '../api/tasks';
import { TaskType, TaskPriority } from '@birdman/core';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from './ui/BrandModal';

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateTaskDto) => void;
  task?: Task | null;
  mode: 'create' | 'edit';
}

export default function TaskModal({ isOpen, onClose, onSubmit, task, mode }: TaskModalProps) {
  const [formData, setFormData] = useState<CreateTaskDto>({
    title: '',
    description: '',
    type: TaskType.GENERAL,
    priority: TaskPriority.MEDIUM,
    dueDate: new Date().toISOString().split('T')[0],
    assignedTo: '',
    relatedEntityId: '',
    relatedEntityType: undefined,
    metadata: {}
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (task && mode === 'edit') {
      setFormData({
        title: task.title,
        description: task.description || '',
        type: task.type,
        priority: task.priority,
        dueDate: task.dueDate.split('T')[0],
        assignedTo: task.assignedTo || '',
        relatedEntityId: task.relatedEntityId || '',
        relatedEntityType: task.relatedEntityType,
        metadata: task.metadata || {}
      });
    } else {
      setFormData({
        title: '',
        description: '',
        type: TaskType.GENERAL,
        priority: TaskPriority.MEDIUM,
        dueDate: new Date().toISOString().split('T')[0],
        assignedTo: '',
        relatedEntityId: '',
        relatedEntityType: undefined,
        metadata: {}
      });
    }
    setErrors({});
  }, [task, mode, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required';
    }

    if (formData.dueDate && new Date(formData.dueDate) < new Date()) {
      newErrors.dueDate = 'Due date cannot be in the past';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const assignedTo = formData.assignedTo?.trim();
    const relatedEntityId = formData.relatedEntityId?.trim();

    onSubmit({
      ...formData,
      dueDate: new Date(formData.dueDate).toISOString(),
      assignedTo: assignedTo || undefined,
      relatedEntityId: relatedEntityId || undefined,
      relatedEntityType: formData.relatedEntityType || undefined,
    });
  };

  const handleInputChange = (field: keyof CreateTaskDto, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader
        title={mode === 'create' ? 'Create New Task' : 'Edit Task'}
        onClose={onClose}
      />
      <form onSubmit={handleSubmit}>
        <BrandModalBody className="space-y-6">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      className={`input mt-1 sm:text-sm ${
                        errors.title ? 'border-red-300' : ''
                      }`}
                      placeholder="Enter task title"
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows={3}
                      className="input mt-1 sm:text-sm"
                      placeholder="Enter task description"
                    />
                  </div>

                  {/* Type and Priority */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700">
                        Type
                      </label>
                      <select
                        value={formData.type}
                        onChange={(e) => handleInputChange('type', e.target.value)}
                        className="mt-1 input text-sm"
                      >
                        <option value={TaskType.BREEDING}>Breeding</option>
                        <option value={TaskType.HEALTH_CHECK}>Health Check</option>
                        <option value={TaskType.BANDING}>Banding</option>
                        <option value={TaskType.FEEDING}>Feeding</option>
                        <option value={TaskType.MAINTENANCE}>Maintenance</option>
                        <option value={TaskType.GENERAL}>General</option>
                        <option value={TaskType.OTHER}>Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700">
                        Priority
                      </label>
                      <select
                        value={formData.priority}
                        onChange={(e) => handleInputChange('priority', e.target.value)}
                        className="mt-1 input text-sm"
                      >
                                                            <option value={TaskPriority.LOW}>Low</option>
                                    <option value={TaskPriority.MEDIUM}>Medium</option>
                                    <option value={TaskPriority.HIGH}>High</option>
                                    <option value={TaskPriority.URGENT}>Urgent</option>
                      </select>
                    </div>
                  </div>

                  {/* Due Date and Assigned To */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700">
                        Due Date *
                      </label>
                      <input
                        type="date"
                        value={formData.dueDate}
                        onChange={(e) => handleInputChange('dueDate', e.target.value)}
                        className={`input mt-1 sm:text-sm ${
                          errors.dueDate ? 'border-red-300' : ''
                        }`}
                      />
                      {errors.dueDate && (
                        <p className="mt-1 text-sm text-red-600">{errors.dueDate}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700">
                        Assigned To
                      </label>
                      <input
                        type="text"
                        value={formData.assignedTo}
                        onChange={(e) => handleInputChange('assignedTo', e.target.value)}
                        className="input mt-1 sm:text-sm"
                        placeholder="Enter assignee name"
                      />
                    </div>
                  </div>

                  {/* Related Entity */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700">
                        Related Entity Type
                      </label>
                      <select
                        value={formData.relatedEntityType || ''}
                        onChange={(e) => handleInputChange('relatedEntityType', e.target.value || undefined)}
                        className="mt-1 input text-sm"
                      >
                        <option value="">None</option>
                        <option value="BIRD">Bird</option>
                        <option value="BREEDING_PAIR">Breeding Pair</option>
                        <option value="CLUTCH">Clutch</option>
                        <option value="EGG">Egg</option>
                        <option value="SPECIES">Species</option>
                        <option value="CAGE">Cage</option>
                        <option value="USER">User</option>
                        <option value="FEED_STOCK">Feed Stock</option>
                        <option value="SUPPLY">Supply</option>
                        <option value="SUPPLIER">Supplier</option>
                        <option value="CUSTOMER">Customer</option>
                        <option value="EQUIPMENT">Equipment</option>
                        <option value="HEALTH_TEST">Health Test</option>
                        <option value="DISEASE_RECORD">Disease Record</option>
                        <option value="DOCUMENT">Document</option>
                        <option value="TRANSACTION">Transaction</option>
                        <option value="ACCOUNT">Account</option>
                        <option value="EXPENSE">Expense</option>
                        <option value="PURCHASE">Purchase</option>
                        <option value="SALE">Sale</option>
                        <option value="NOTE">Note</option>
                        <option value="PHOTO">Photo</option>
                        <option value="SYSTEM_SETTING">System Setting</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700">
                        Related Entity ID
                      </label>
                      <input
                        type="text"
                        value={formData.relatedEntityId}
                        onChange={(e) => handleInputChange('relatedEntityId', e.target.value)}
                        className="input mt-1 sm:text-sm"
                        placeholder="Enter entity ID"
                      />
                    </div>
                  </div>

                  {/* Metadata */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Additional Notes
                    </label>
                    <textarea
                      value={formData.metadata?.notes || ''}
                      onChange={(e) => handleInputChange('metadata', {
                        ...formData.metadata,
                        notes: e.target.value
                      })}
                      rows={2}
                      className="input mt-1 sm:text-sm"
                      placeholder="Enter additional notes or instructions"
                    />
                  </div>

        </BrandModalBody>
        <BrandModalFooter>
          <button type="button" onClick={onClose} className="btn btn-secondary text-sm">
            Cancel
          </button>
          <button type="submit" className="btn btn-primary text-sm">
            {mode === 'create' ? 'Create Task' : 'Update Task'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
} 